import { Button, Group, Stack, Text } from '@mantine/core';
import { hideNotification, NotificationData, showNotification } from '@mantine/notifications';
import { IconUser } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '@/components/Notification/hooks';
import { useGPTStore } from '@/store';

import { getAdminWebSocket } from './hooks/adminWebsocket';

interface ClientNotificationParams {
  name: string;
  url: string;
  aiId: string;
  connectionId: string;
}

export default () => {
  const { t } = useTranslation();
  const { userInfo, quotaInfo } = useGPTStore((state) => state);
  const navigate = useNavigate();
  const { setVisitorList } = useNotification();
  const webSocketClient = getAdminWebSocket();

  const audioRef = useRef<HTMLAudioElement>(null);

  const playNotificationSound = (): void => {
    const startPlayPromise = audioRef.current?.play();
    if (startPlayPromise !== undefined) {
      startPlayPromise
        .catch((error) => {
          const id = 'play-audio-error';
          hideNotification(id);
          if (error.name === 'NotAllowedError') {
            // // 处理浏览器拒绝自动播放的情况
            // showNotification({
            //   id,
            //   autoClose: false,
            //   message: (
            //     <Collapse in={true}>
            //       <Group
            //         justify="space-between"
            //         className="flex items-center bg-[#FEC84B] py-3 px-5 rounded-[8px] shadow-lg"
            //         mb={40}
            //       >
            //         <Text size="sm">{t('dashboard.widget_install_tip')}</Text>
            //         <Group className="space-x-3">
            //           <UnstyledButton
            //             className="text-sm bg-white hover:bg-[#fbeed0] active:bg-[#ffe9b7] text-[#F79009] rounded-[8px] px-[8px] py-[4px]"
            //             // onClick={openWidget}
            //           >
            //             {t('dashboard.install')}
            //           </UnstyledButton>
            //           <CloseButton
            //             aria-label="Close modal"
            //             className="text-[#F79009] bg-white hover:bg-[#fbeed0] active:bg-[#ffe9b7] rounded-full"
            //             // onClick={() => setOpened((o) => !o)}
            //           />
            //         </Group>
            //       </Group>
            //     </Collapse>
            //   ),
            // });
          } else {
            // 处理加载或播放错误
          }
        })
        .then(() => {
          // 仅在播放开始后才开始执行你需要执行的操作。
        });
    }
  };

  const showNotificationWithSound = (notificationProps: NotificationData) => {
    // TODO: 暂时屏蔽访客通知音效，后续可能恢复
    // playNotificationSound();
    showNotification(notificationProps);
  };

  const clientNotification = (params: ClientNotificationParams) => {
    const { aiId, connectionId, name } = params;
    hideNotification(connectionId);
    showNotificationWithSound({
      id: connectionId,
      icon: null,
      color: 'teal',
      message: (
        <Group>
          <IconUser size={20} />
          <Stack gap={0}>
            <Text size={'sm'}>{name}</Text>
            <Text size={'sm'}>{t('visitors.accessing_page')}</Text>
          </Stack>
          <Button
            variant="white"
            size="xs"
            color="green"
            onClick={() => {
              navigate(`/bot/${aiId}/visitors`, {
                state: { connectionId },
              });
              hideNotification(connectionId);
            }}
          >
            {t('create_v2.documents.details')}
          </Button>
        </Group>
      ),

      styles: (theme) => ({
        root: {
          backgroundColor: theme.colors.green[6],
          borderColor: theme.colors.green[6],
          paddingLeft: 15,

          '&::before': { display: 'none' },
        },

        title: { color: theme.white },
        description: { color: theme.white },
        closeButton: {
          color: theme.white,
          '&:hover': { backgroundColor: theme.colors.green[7] },
        },
      }),
    });
  };

  const initAdminSocket = () => {
    // 注册事件监听器
    webSocketClient.on('open', () => {
      webSocketClient.send({
        type: 'request',
        cmd: 'login_req',
        data: {
          ai_id: '', // 监听多个bot，账号级别不需要ai_id
          user_id: userInfo?.user_id,
          page_url: window.location.href,
        },
      });
    });

    webSocketClient.on('message', (message) => {
      if (message.type !== 'notification') return;

      switch (message.event) {
        case 'login': {
          const { ai_id, connection_id, email, page_url, toast, user_id } = message.data;
          setVisitorList((state) => [...new Set([...state, message.data])]);

          if (toast) {
            clientNotification({
              name: email || user_id,
              url: page_url,
              aiId: ai_id,
              connectionId: connection_id,
            });
          }

          break;
        }
        case 'logout':
          {
            const { connection_id } = message.data;
            setVisitorList((state) => state.filter((item) => item.connection_id !== connection_id));
            hideNotification(connection_id);
          }
          break;
        default:
          break;
      }
    });

    webSocketClient.start();
  };

  useEffect(() => {
    if (userInfo && quotaInfo) {
      initAdminSocket();
    }

    return () => {
      // 在不需要 WebSocket 连接的时候关闭
      webSocketClient.stop();
    };
  }, [userInfo, quotaInfo]);

  return <audio style={{ display: 'none' }} ref={audioRef} src="/files/3.m4a" />;
};
